import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContentRestoreRevisionOutput } from '@vivport/backend/types';
import { listRevisions, readRevision, restoreRevision } from '@/api/revisions-api';
import { contentKeys } from './useContentQueries';
import { provenanceKeys } from './useProvenanceQueries';

export const revisionKeys = {
  all: ['revisions'] as const,
  list: (contentId: number) => [...revisionKeys.all, 'list', contentId] as const,
  snapshot: (contentId: number) => [...revisionKeys.all, 'snapshot', contentId] as const,
};

export const useRevisionList = (contentId: number | null) =>
  useQuery({
    queryKey: contentId === null ? [...revisionKeys.all, 'list', 'disabled'] : revisionKeys.list(contentId),
    queryFn: () => listRevisions({ contentId: contentId as number }),
    enabled: contentId !== null,
  });

export const useRevisionSnapshot = (contentId: number | null) =>
  useQuery({
    queryKey: contentId === null ? [...revisionKeys.all, 'snapshot', 'disabled'] : revisionKeys.snapshot(contentId),
    queryFn: () => readRevision({ contentId: contentId as number }),
    enabled: contentId !== null,
  });

export const useRestoreRevision = () => {
  const queryClient = useQueryClient();
  return useMutation<ContentRestoreRevisionOutput, Error, { contentId: number; code: string }>({
    mutationFn: ({ contentId }) => restoreRevision({ contentId }),
    onSuccess: async (_output, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: revisionKeys.all }),
        queryClient.invalidateQueries({ queryKey: contentKeys.details(), refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: [...contentKeys.all, 'documentRender'] }),
        queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
        }),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: provenanceKeys.listFor(variables.code) }),
      ]);
    },
  });
};
